'use client';

import { ComponentProps } from 'react';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDownIcon } from 'lucide-react';

import { cn } from '@/lib/utils/cn';

type T_Variant = 'default' | 'brand' | 'destructive';

function Accordion({
  variant = 'brand',
  ...props
}: ComponentProps<typeof AccordionPrimitive.Root> & {
  variant?: T_Variant;
}) {
  const variantClasses: Record<T_Variant, string> = {
    default: 'border-b-1 border-border',
    brand: '',
    destructive: ''
  };

  return (
    <AccordionPrimitive.Root
      className={cn(variantClasses[variant])}
      data-slot="accordion"
      {...props}
    />
  );
}

function AccordionItem({
  className,
  variant = 'brand',
  ...props
}: ComponentProps<typeof AccordionPrimitive.Item> & {
  variant?: T_Variant;
}) {
  const variantClasses: Record<T_Variant, string> = {
    default: '',
    destructive: '',
    brand:
      'rounded-lg data-[state=open]:border-1 data-[state=open]:border-accordion-brand data-[state=open]:border-b-2 data-[state=open]:border-b-primary '
  };

  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn(
        'border-b last:border-b-0',
        variantClasses[variant],
        className
      )}
      {...props}
    />
  );
}

function AccordionTrigger({
  variant = 'brand',
  className,
  children,
  headingLevel = 3,
  ...props
}: ComponentProps<typeof AccordionPrimitive.Trigger> & {
  variant?: T_Variant;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
}) {
  const variantClasses: Record<T_Variant, string> = {
    default:
      'focus:outline-none focus:ring-3 focus:ring-ring active:text-muted active:ring-0 active:no-underline ',
    brand:
      'border-1 border-accordion-brand py-4 px-5 hover:text-primary text-md ' +
      ' focus:outline-none focus:ring-3 focus:ring-ring active:text-muted active:no-underline active:ring-0 active:text-base data-[state=closed]:hover:underline data-[state=closed]:font-normal ' +
      ' data-[state=open]:bg-accordion-brand data-[state=open]:rounded-bl-none data-[state=open]:rounded-br-none data-[state=open]:font-bold ' +
      ' hover:data-[state=open]:no-underline  active:data-[state=open]:font-normal data-[state=open]:active:font-bold',
    destructive:
      'focus:outline-none active:text-muted active:ring-0 active:no-underline border border-destructive rounded-xl bg-destructive/50 pt-5 pb-4 px-5 ' +
      'border-b-0 rounded-bl-none rounded-br-none '
  };

  const chevronClasses: Record<T_Variant, string> = {
    default: 'text-muted-foreground',
    brand: 'text-primary-foreground',
    destructive: 'text-destructive-foreground'
  };

  const HeadingTag = `h${headingLevel}` as const;

  return (
    <AccordionPrimitive.Header asChild>
      <HeadingTag className="flex">
        <AccordionPrimitive.Trigger
          data-slot="accordion-trigger"
          className={cn(
            'cursor-pointer flex flex-1 justify-between items-center gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:ring-3 disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180',
            variantClasses[variant],
            className
          )}
          {...props}
        >
          {children}
          <ChevronDownIcon
            className={cn(
              'text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200',
              chevronClasses[variant]
            )}
          />
        </AccordionPrimitive.Trigger>
      </HeadingTag>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  variant = 'brand',
  className,
  children,
  ...props
}: ComponentProps<typeof AccordionPrimitive.Content> & {
  variant?: T_Variant;
}) {
  const variantClasses: Record<T_Variant, string> = {
    default: 'border-b-1 border-border py-2',
    brand: 'p-5 text-md',
    destructive:
      'border-1 border-t-0 border-b-0 border-destructive  rounded-tr-none rounded-tl-none bg-destructive/50 pt-5 pb-4 px-5 text-md'
  };

  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className={cn(
        'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm',
        variantClasses[variant]
      )}
      {...props}
    >
      <div className={cn('pt-0 pb-1', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
